import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../../contexts/AuthContext";
import "./Sugestoes.css";

const STATUS = {
  NOVA: "Nova",
  EM_ANALISE: "Em análise",
  SERA_FEITO: "Será feito",
  CONCLUIDA: "Concluída",
};

const formatarData = (valor) => {
  if (!valor) return "--";
  const d = new Date(valor);
  if (!Number.isFinite(d.getTime())) return "--";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};

export default function Sugestoes() {
  const { authInfo, setNotiMessage } = useContext(AuthContext);
  const dataUser = authInfo?.dataUser;
  const token = dataUser?.token;
  const admin = dataUser?.key === "ADMIN";
  const servidor = process.env.REACT_APP_SERVIDOR;

  const [carregando, setCarregando] = useState(true);
  const [itens, setItens] = useState([]);
  const [filtro, setFiltro] = useState("TODAS");
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [respostas, setRespostas] = useState({});
  const [statusEdicao, setStatusEdicao] = useState({});

  const headers = useMemo(() => ({
    "x-access-token": token,
    "content-type": "application/json",
  }), [token]);

  const carregar = async () => {
    if (!token) return;
    setCarregando(true);
    try {
      const rota = admin ? "/admin-2026/sugestoes" : "/sugestoes-2026/cliente";
      const r = await axios.get(`${servidor}${rota}`, { headers });
      const lista = Array.isArray(r.data) ? r.data : [];
      setItens(lista);
      const respostasIniciais = {};
      const statusIniciais = {};
      lista.forEach((item) => {
        respostasIniciais[item.id] = item.resposta || "";
        statusIniciais[item.id] = item.status || "NOVA";
      });
      setRespostas(respostasIniciais);
      setStatusEdicao(statusIniciais);
    } catch (e) {
      setNotiMessage({ type: "error", message: e?.response?.data?.error || "Não foi possível carregar as sugestões." });
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    const timer = setInterval(carregar, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, admin]);

  const enviar = async (event) => {
    event.preventDefault();
    if (titulo.trim().length < 3 || descricao.trim().length < 5) {
      setNotiMessage({ type: "warning", message: "Informe um título e descreva melhor a sugestão." });
      return;
    }
    setEnviando(true);
    try {
      await axios.post(`${servidor}/sugestoes-2026/cliente`, {
        titulo: titulo.trim(),
        descricao: descricao.trim(),
      }, { headers });
      setTitulo("");
      setDescricao("");
      setNotiMessage({ type: "success", message: "Sugestão enviada. Ela já está visível para todos." });
      await carregar();
    } catch (e) {
      setNotiMessage({ type: "error", message: e?.response?.data?.error || "Não foi possível enviar a sugestão." });
    } finally {
      setEnviando(false);
    }
  };

  const salvarResposta = async (item) => {
    const resposta = String(respostas[item.id] || "").trim();
    if (resposta.length < 2) {
      setNotiMessage({ type: "warning", message: "Digite a resposta antes de salvar." });
      return;
    }
    try {
      await axios.post(`${servidor}/admin-2026/sugestoes/${item.id}/responder`, {
        resposta,
        status: statusEdicao[item.id] || "SERA_FEITO",
      }, { headers });
      setNotiMessage({ type: "success", message: "Resposta publicada para os clientes." });
      await carregar();
    } catch (e) {
      setNotiMessage({ type: "error", message: e?.response?.data?.error || "Não foi possível salvar a resposta." });
    }
  };

  const alterarStatus = async (item, novoStatus) => {
    setStatusEdicao((atual) => ({ ...atual, [item.id]: novoStatus }));
    try {
      await axios.post(`${servidor}/admin-2026/sugestoes/${item.id}/status`, { status: novoStatus }, { headers });
      setItens((atual) => atual.map((x) => x.id === item.id ? { ...x, status: novoStatus } : x));
    } catch (e) {
      setNotiMessage({ type: "error", message: e?.response?.data?.error || "Não foi possível alterar o status." });
      await carregar();
    }
  };

  const marcarLida = async (item) => {
    try {
      await axios.post(`${servidor}/sugestoes-2026/cliente/${item.id}/ler`, {}, { headers });
      setItens((atual) => atual.map((x) => x.id === item.id ? { ...x, respostaNova: false, lidoPeloClienteEm: new Date().toISOString() } : x));
    } catch (e) {
      setNotiMessage({ type: "error", message: e?.response?.data?.error || "Não foi possível marcar a resposta como lida." });
    }
  };

  const excluir = async (item) => {
    if (!window.confirm(`Excluir a sugestão “${item.titulo}” para todos os clientes?`)) return;
    try {
      await axios.delete(`${servidor}/admin-2026/sugestoes/${item.id}`, { headers });
      setItens((atual) => atual.filter((x) => x.id !== item.id));
      setNotiMessage({ type: "success", message: "Sugestão excluída para todos." });
    } catch (e) {
      setNotiMessage({ type: "error", message: e?.response?.data?.error || "Não foi possível excluir a sugestão." });
    }
  };

  const contadores = useMemo(() => ({
    novas: itens.filter((x) => x.status === "NOVA").length,
    analise: itens.filter((x) => x.status === "EM_ANALISE").length,
    seraFeito: itens.filter((x) => x.status === "SERA_FEITO").length,
    concluidas: itens.filter((x) => x.status === "CONCLUIDA").length,
  }), [itens]);

  const exibidos = !admin && filtro === "MINHAS" ? itens.filter((x) => x.minha) : itens;

  return (
    <div className="sugestoes-page">
      <div className="sugestoes-topo">
        <div>
          <span className="sugestoes-kicker">MELHORIAS DO SISTEMA</span>
          <h1>Sugestões</h1>
          <p>
            {admin
              ? "Acompanhe as ideias enviadas pelas empresas, responda e exclua quando o assunto estiver encerrado."
              : "Envie ideias de melhoria e acompanhe também as sugestões das outras empresas."}
          </p>
        </div>
        <button type="button" className="sugestoes-atualizar" onClick={carregar} disabled={carregando}>
          <i className="bi bi-arrow-clockwise" /> {carregando ? "Atualizando..." : "Atualizar"}
        </button>
      </div>

      {admin ? (
        <div className="sugestoes-resumo">
          <div><span>Novas</span><strong>{contadores.novas}</strong></div>
          <div><span>Em análise</span><strong>{contadores.analise}</strong></div>
          <div><span>Será feito</span><strong>{contadores.seraFeito}</strong></div>
          <div><span>Concluídas</span><strong>{contadores.concluidas}</strong></div>
        </div>
      ) : (
        <>
          <form className="sugestao-form" onSubmit={enviar}>
            <h2>Enviar nova sugestão</h2>
            <label>
              Título
              <input
                value={titulo}
                maxLength={120}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex.: Melhorar relatório de pagamentos"
              />
            </label>
            <label>
              Descrição
              <textarea
                value={descricao}
                maxLength={2000}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Explique como você gostaria que essa melhoria funcionasse."
                rows={5}
              />
            </label>
            <div className="sugestao-form-footer">
              <small>O nome da sua empresa e a data são adicionados automaticamente.</small>
              <button type="submit" disabled={enviando}>{enviando ? "Enviando..." : "Enviar sugestão"}</button>
            </div>
          </form>

          <div className="sugestoes-filtros">
            <button type="button" className={filtro === "TODAS" ? "active" : ""} onClick={() => setFiltro("TODAS")}>Todas</button>
            <button type="button" className={filtro === "MINHAS" ? "active" : ""} onClick={() => setFiltro("MINHAS")}>Minhas sugestões</button>
          </div>
        </>
      )}

      <div className="sugestoes-lista">
        {!carregando && exibidos.length === 0 && (
          <div className="sugestoes-vazio">Ainda não há sugestões nesta lista.</div>
        )}

        {exibidos.map((item) => (
          <article className={`sugestao-card ${item.respostaNova ? "tem-resposta-nova" : ""}`} key={item.id}>
            <div className="sugestao-card-topo">
              <div>
                <span className="sugestao-empresa">{item.empresa}</span>
                <h2>{item.titulo}</h2>
              </div>
              <span className={`sugestao-status status-${String(item.status || "NOVA").toLowerCase()}`}>
                {STATUS[item.status] || item.status}
              </span>
            </div>

            <p className="sugestao-descricao">{item.descricao}</p>
            <small className="sugestao-data">Enviada em {formatarData(item.criadoEm)}</small>

            {item.resposta && (
              <div className="sugestao-resposta">
                <div className="sugestao-resposta-titulo">
                  <strong>Resposta da Stronda Music</strong>
                  {item.respostaNova && <span>Resposta nova</span>}
                </div>
                <p>{item.resposta}</p>
                {item.respondidoEm && <small>Respondida em {formatarData(item.respondidoEm)}</small>}
                {!admin && item.minha && item.respostaNova && (
                  <button type="button" className="sugestao-lida" onClick={() => marcarLida(item)}>Marcar resposta como lida</button>
                )}
              </div>
            )}

            {admin && (
              <div className="sugestao-admin">
                <label>
                  Status
                  <select value={statusEdicao[item.id] || item.status || "NOVA"} onChange={(e) => alterarStatus(item, e.target.value)}>
                    <option value="NOVA">Nova</option>
                    <option value="EM_ANALISE">Em análise</option>
                    <option value="SERA_FEITO">Será feito</option>
                    <option value="CONCLUIDA">Concluída</option>
                  </select>
                </label>
                <label className="sugestao-admin-resposta">
                  Resposta para os clientes
                  <textarea
                    rows={3}
                    maxLength={2000}
                    value={respostas[item.id] || ""}
                    onChange={(e) => setRespostas((atual) => ({ ...atual, [item.id]: e.target.value }))}
                    placeholder="Ex.: Será feito na próxima atualização."
                  />
                </label>
                <div className="sugestao-admin-acoes">
                  <button type="button" className="salvar" onClick={() => salvarResposta(item)}>Publicar resposta</button>
                  <button type="button" className="excluir" onClick={() => excluir(item)}>Excluir para todos</button>
                </div>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
